/**
 * Dispatcher view: a pressable row plus a Modal hosting the mode-specific
 * picker. Modal-based hosting keeps the picker usable inside the Settings
 * submenu without taking over the menu's vertical space — especially for
 * `all` mode where the typeahead needs a tall result list.
 *
 * The Modal also exposes a "Clear active team" row when one is set, so
 * the user can return to the org-wide view without picking a different
 * team first.
 *
 * On native the trigger row lives inside the header Settings menu, which
 * renders in a native `RNModal` layer (`HeaderPopover` passes `useRNModal`
 * to its `Popover`). A plain GlueStack Modal portals into the JS overlay
 * host, which sits *below* that native layer — so the picker opened behind
 * the menu and was unreachable (SE-4952). Passing `useRNModal` makes the
 * picker its own native modal nested inside the menu's; nested native
 * modals stack, so the picker now presents above the menu. Web is
 * unaffected and keeps the JS-portal path.
 * @module features/team-selection/components/TeamSelector/TeamSelectorView
 */
import { ChevronRightIcon, UsersIcon, XIcon } from "lucide-react-native";
import React, { memo } from "react";
import { Platform } from "react-native";

import { Heading } from "@/components/atoms/Heading";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
} from "@/components/atoms/Modal";
import { Pressable } from "@/components/atoms/Pressable";
import { Text } from "@/components/atoms/Text";

import TeamSelectorAll from "@/features/team-selection/components/molecules/TeamSelectorAll";
import TeamSelectorLinked from "@/features/team-selection/components/molecules/TeamSelectorLinked";
import { TeamSelectionMode } from "@/generated/graphql";

/**
 * Render the picker in a native `RNModal` on iOS/Android so it stacks
 * above the header menu's native modal layer. Web keeps the default
 * JS-portal Modal, which already layers correctly there.
 */
const PICKER_USES_RN_MODAL = Platform.OS !== "web";

/**
 * Props for {@link TeamSelectorView}.
 */
export interface TeamSelectorViewProps {
  readonly mode: TeamSelectionMode;
  readonly label: string;
  readonly isOpen: boolean;
  readonly onOpen: () => void;
  readonly onClose: () => void;
  /** True when an active team is currently set. Drives the Clear row. */
  readonly hasActiveTeam: boolean;
  /** Clears the active team and closes the modal. */
  readonly onClear: () => void;
}

/**
 * Dispatcher view.
 * @param root0 - Component props
 * @param root0.mode - Active org's team-selection mode (linked or all)
 * @param root0.label - Trigger row label (current team name or placeholder)
 * @param root0.isOpen - Modal open state
 * @param root0.onOpen - Handler invoked when the trigger row is pressed
 * @param root0.onClose - Handler invoked when the modal should close
 * @param root0.hasActiveTeam - Whether to render the Clear row inside the modal
 * @param root0.onClear - Handler invoked when the user presses "Clear active team"
 * @returns The rendered dispatcher
 */
const TeamSelectorView = ({
  mode,
  label,
  isOpen,
  onOpen,
  onClose,
  hasActiveTeam,
  onClear,
}: TeamSelectorViewProps) => (
  <>
    <Pressable
      testID="team-selector-trigger"
      accessibilityRole="button"
      accessibilityLabel={`Active team: ${label}`}
      onPress={onOpen}
    >
      <Stack
        direction="horizontal"
        className="items-center justify-between px-4 py-3"
      >
        <Stack direction="horizontal" className="items-center" space="2">
          <Icon as={UsersIcon} className="text-content-muted" size="sm" />
          <Text variant="unstyled" className="text-content-secondary">
            Active Team
          </Text>
        </Stack>
        <Stack direction="horizontal" className="items-center" space="1">
          <Text variant="unstyled" className="text-content-muted">
            {label}
          </Text>
          <Icon
            as={ChevronRightIcon}
            className="text-content-subtle"
            size="sm"
          />
        </Stack>
      </Stack>
    </Pressable>

    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      useRNModal={PICKER_USES_RN_MODAL}
    >
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading variant="title">Select active team</Heading>
          <ModalCloseButton onPress={onClose}>
            <Icon as={XIcon} />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <Stack space="3">
            {hasActiveTeam ? (
              <Pressable
                testID="team-selector-clear"
                accessibilityRole="button"
                accessibilityLabel="Clear active team"
                onPress={onClear}
              >
                <Stack
                  direction="horizontal"
                  className="items-center justify-between rounded-none border border-outline-default bg-surface-raised p-3"
                >
                  <Text variant="unstyled" className="text-content-secondary">
                    Clear active team
                  </Text>
                  <Icon as={XIcon} className="text-content-muted" size="sm" />
                </Stack>
              </Pressable>
            ) : null}
            {mode === TeamSelectionMode.Linked ? (
              <TeamSelectorLinked onSelected={onClose} />
            ) : (
              <TeamSelectorAll onSelected={onClose} />
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  </>
);

TeamSelectorView.displayName = "TeamSelectorView";

export default memo(TeamSelectorView);
